import {
  ErrorResponse,
  MembershipPackage,
  MembershipPackageDiscontinueParams,
  MembershipPackageList,
  MembershipStatus,
  SessionsApi,
} from '@citruscamps/citrus-client'
import { Dispatch } from 'react'
import { FetchNextPageOptions, InfiniteQueryObserverResult } from 'react-query'
import { DefaultDetailsPagination, DefaultSearchPagination } from '../constants/pagination'
import { Pagination } from '../interfaces/pagination'
import { useUpdateMembershipPackages } from '../pages/Settings/pages/ManagePackages/hooks/useUpdateMemberPackages'
import { generateApiConfig } from '../utils/client-config'
import { IFetchManyProps, useFetchInfiniteMany, useFetchMany } from './useFetchMany'

export const DefaultMembershipPackageFetchProps: MembershipPackageFetchProps = {
  sort: 'created',
  order: 'DESC',
  query: {
    is_discontinued: false,
  },
}

export interface MembershipPackageFilterProps {
  is_discontinued?: boolean
  id?: string[]
  exclude_id?: string[]
  program_attendee_id?: string
  status?: MembershipStatus[]
}

export interface MembershipPackageFetchProps {
  sort?: string
  order?: 'ASC' | 'DESC'
  query: MembershipPackageFilterProps
}

type IProps = Pick<
  IFetchManyProps<MembershipPackage, MembershipPackageFilterProps>,
  'programId' | 'fetchProps' | 'search' | 'pagination' | 'isEnabled' | 'setFetchProps'
>

interface FetchMembershipPackages {
  data: MembershipPackage[]
  error?: ErrorResponse
  fetchProps: MembershipPackageFetchProps
  isError: boolean
  isLoading: boolean
  pagination: Pagination
  search: string
  setFetchProps: (value?: MembershipPackageFetchProps) => void
  setSearch: Dispatch<string>
  setPagination: Dispatch<Pagination>
  handleUpdate: (value: MembershipPackage) => Promise<void>
  handleDelete: (value: MembershipPackage) => Promise<void>
  handleDiscontinue: (value: MembershipPackageDiscontinueParams) => Promise<void>
  handleRecontinue: (value: MembershipPackage) => Promise<void>
}

export const useFetchMembershipPackages = (props: IProps): FetchMembershipPackages => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setPagination,
    setSearch,
    setFetchProps,
  } = useFetchMany<MembershipPackage, MembershipPackageFilterProps>({
    type: 'membership_package',
    programId: props.programId,
    fetchProps: props.fetchProps,
    search: props.search,
    defaultFetchProps: DefaultMembershipPackageFetchProps,
    defaultPagination: props.pagination || DefaultSearchPagination,
    isEnabled: props.isEnabled,
    setFetchProps: props.setFetchProps,
    queryFn: async (queryParams): Promise<MembershipPackageList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new SessionsApi(generateApiConfig())
      return await client.findMembershipPackages(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          is_discontinued:
            typeof query?.is_discontinued !== 'undefined' ? query?.is_discontinued : undefined,
          search: search || undefined,
          id: !!query?.id && query.id?.length > 0 ? query.id : undefined,
          exclude_id:
            !!query?.exclude_id && query.exclude_id?.length > 0 ? query.exclude_id : undefined,
          program_attendee_id: query?.program_attendee_id || undefined,
          status: !!query?.status && query.status?.length > 0 ? query.status : undefined,
        },
        { signal },
      )
    },
  })
  const { handleUpdate, handleDelete, handleDiscontinue, handleRecontinue } =
    useUpdateMembershipPackages({ programId: props.programId })

  return {
    data,
    fetchProps,
    error,
    isError,
    isLoading,
    pagination,
    search: _search,
    handleUpdate,
    handleDelete,
    handleDiscontinue,
    handleRecontinue,
    setFetchProps,
    setPagination,
    setSearch,
  }
}

interface IInfiniteProps extends IProps {
  fetchAll?: boolean
}

interface FetchInfiniteMembershipPackages
  extends Omit<FetchMembershipPackages, 'setPagination' | 'setFetchProps'> {
  isFetching: boolean
  isFetchingNextPage: boolean
  hasNextPage: boolean
  fetchNextPage: (
    options?: FetchNextPageOptions | undefined,
  ) => Promise<InfiniteQueryObserverResult<MembershipPackageList, ErrorResponse>>
}
export const useFetchInfiniteMembershipPackages = ({
  programId,
  ...props
}: IInfiniteProps): FetchInfiniteMembershipPackages => {
  const {
    data,
    error,
    fetchProps,
    isError,
    isLoading,
    pagination,
    search: _search,
    setSearch,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage = false,
  } = useFetchInfiniteMany<MembershipPackage, MembershipPackageFilterProps>({
    type: 'membership_package',
    programId,
    search: props.search,
    fetchProps: props.fetchProps || DefaultMembershipPackageFetchProps,
    pagination: props.pagination || DefaultDetailsPagination,
    fetchAll: props.fetchAll,
    isEnabled: props.isEnabled,
    queryFn: async (queryParams): Promise<MembershipPackageList> => {
      const { programId, limit, skip, query, sort, order, search, signal } = queryParams
      const client = new SessionsApi(generateApiConfig())
      return await client.findMembershipPackages(
        {
          program_id: programId,
          limit,
          skip,
          order,
          sort,
          is_discontinued:
            typeof query?.is_discontinued !== 'undefined' ? query?.is_discontinued : undefined,
          search: search || undefined,
          id: query.id,
          exclude_id:
            !!query?.exclude_id && query.exclude_id?.length > 0 ? query.exclude_id : undefined,
          program_attendee_id: query?.program_attendee_id || undefined,
          status: !!query?.status && query.status?.length > 0 ? query.status : undefined,
        },
        { signal },
      )
    },
  })
  const { handleUpdate, handleDelete, handleDiscontinue, handleRecontinue } =
    useUpdateMembershipPackages({ programId })

  return {
    data,
    error,
    fetchProps,
    hasNextPage,
    isError,
    isFetching,
    isFetchingNextPage,
    isLoading,
    pagination,
    search: _search,
    handleUpdate,
    handleDelete,
    handleDiscontinue,
    handleRecontinue,
    fetchNextPage,
    setSearch,
  }
}
